import React, { useCallback, useEffect, useState } from 'react';
import PageHeader from '@/components/PageHeader';
import AdminBillingNav from '@/components/adminBilling/AdminBillingNav';
import BillingPlanFormModal from '@/components/adminBilling/BillingPlanFormModal';
import StatusChip from '@/components/adminBilling/StatusChip';
import ConfirmActionModal from '@/components/ConfirmActionModal';
import { useToast } from '@/components/Toast';
import { billingAdminService, type BillingPlan } from '@/api/services/billingAdmin';
import { formatCents } from '@/api/services/adminBilling';
import { formatApiError } from '@/utils/formatApiError';
import {
  billingPlanToDraft,
  emptyBillingPlanDraft,
  formatQuota,
  type BillingPlanDraft,
} from '@/utils/billingPlanForm';
import { useMe } from '@/context/MeContext';
import { DataTableScrollArea } from '@/components/table';

type ModalState =
  | { mode: 'create'; draft: BillingPlanDraft }
  | { mode: 'edit'; planId: string; draft: BillingPlanDraft }
  | null;

export default function AdminBillingPlansPage() {
  const { me } = useMe();
  const { toast } = useToast();
  const [items, setItems] = useState<BillingPlan[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState<ModalState>(null);
  const [confirmDelete, setConfirmDelete] = useState<BillingPlan | null>(null);
  const [confirmArchive, setConfirmArchive] = useState<BillingPlan | null>(null);
  const [saving, setSaving] = useState(false);

  const canManage = Boolean(me?.permissions.canManageAdminBilling);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await billingAdminService.listPlans(true);
      setItems(res.items);
      setError(null);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const openCreate = () => setModal({ mode: 'create', draft: emptyBillingPlanDraft() });

  const openEdit = (plan: BillingPlan) =>
    setModal({ mode: 'edit', planId: plan.id, draft: billingPlanToDraft(plan) });

  const saveModal = async (payload: ReturnType<typeof import('@/utils/billingPlanForm').buildBillingPlanPayload>) => {
    if (!modal) return;
    setSaving(true);
    try {
      if (modal.mode === 'create') {
        await billingAdminService.createPlan(payload);
        toast('Plan created', 'success');
      } else {
        await billingAdminService.updatePlan(modal.planId, payload);
        toast('Plan updated', 'success');
      }
      setModal(null);
      await load();
    } catch (e) {
      throw new Error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const duplicatePlan = async (plan: BillingPlan) => {
    try {
      await billingAdminService.duplicatePlan(plan.id);
      toast('Plan duplicated', 'success');
      await load();
    } catch (e) {
      toast(formatApiError(e), 'error');
    }
  };

  const toggleActive = async (plan: BillingPlan) => {
    try {
      if (plan.isActive) {
        await billingAdminService.deactivatePlan(plan.id);
        toast('Plan deactivated', 'success');
      } else {
        await billingAdminService.activatePlan(plan.id);
        toast('Plan activated', 'success');
      }
      await load();
    } catch (e) {
      toast(formatApiError(e), 'error');
    }
  };

  const removePlan = async (plan: BillingPlan) => {
    const inUse = (plan.subscriptionCount ?? 0) > 0;
    if (inUse) {
      setConfirmArchive(plan);
      return;
    }
    setConfirmDelete(plan);
  };

  const executeDeletePlan = async () => {
    if (!confirmDelete) return;
    try {
      await billingAdminService.deletePlan(confirmDelete.id);
      toast('Plan deleted', 'success');
      await load();
    } catch (e) {
      toast(formatApiError(e), 'error');
    } finally {
      setConfirmDelete(null);
    }
  };

  const executeArchivePlan = async () => {
    if (!confirmArchive) return;
    await toggleActive(confirmArchive);
    setConfirmArchive(null);
  };

  return (
    <div className="admin-billing-page">
      <AdminBillingNav />
      <PageHeader
        title="Billing plans"
        subtitle="Create and manage package catalog, quotas, pricing, and display metadata."
      >
        {canManage ? (
          <button type="button" className="btn btn-primary" onClick={openCreate}>
            New plan
          </button>
        ) : null}
      </PageHeader>

      {error ? <div className="alert alert-error">{error}</div> : null}

      <section className="panel">
        {loading ? <p className="muted">Loading plans…</p> : null}
        <DataTableScrollArea>
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Slug</th>
              <th>Price</th>
              <th>Currency</th>
              <th>Interval</th>
              <th>Max searches</th>
              <th>Max sources/search</th>
              <th>Notification limit/day</th>
              <th>Active</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((plan) => (
              <tr key={plan.id}>
                <td>{plan.name}</td>
                <td>{plan.slug}</td>
                <td>{formatCents(plan.priceCents, plan.currency)}</td>
                <td>{plan.currency}</td>
                <td>{plan.billingInterval}</td>
                <td>{plan.maxSearches}</td>
                <td>{formatQuota(plan.maxSourcesPerSearch)}</td>
                <td>{formatQuota(plan.maxNotificationsPerDay)}</td>
                <td>
                  <StatusChip status={plan.isActive ? 'active' : 'expired'} />
                </td>
                <td>
                  {canManage ? (
                    <div className="table-action-bar">
                      <button type="button" className="btn btn-ghost btn-sm" onClick={() => openEdit(plan)}>
                        Edit
                      </button>
                      <button type="button" className="btn btn-ghost btn-sm" onClick={() => void duplicatePlan(plan)}>
                        Duplicate
                      </button>
                      <button type="button" className="btn btn-ghost btn-sm" onClick={() => void toggleActive(plan)}>
                        {plan.isActive ? 'Deactivate' : 'Activate'}
                      </button>
                      <button type="button" className="btn btn-ghost btn-sm" onClick={() => void removePlan(plan)}>
                        {(plan.subscriptionCount ?? 0) > 0 ? 'Archive' : 'Delete'}
                      </button>
                    </div>
                  ) : (
                    <span className="muted">View only</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </DataTableScrollArea>
      </section>

      {modal ? (
        <BillingPlanFormModal
          mode={modal.mode}
          draft={modal.draft}
          onChange={(draft) => setModal({ ...modal, draft })}
          onClose={() => setModal(null)}
          onSubmit={saveModal}
          saving={saving}
        />
      ) : null}
      <ConfirmActionModal
        open={Boolean(confirmDelete)}
        title={`Delete plan "${confirmDelete?.name ?? ''}"?`}
        description="This cannot be undone."
        confirmLabel="Delete plan"
        variant="danger"
        onCancel={() => setConfirmDelete(null)}
        onConfirm={executeDeletePlan}
      />
      <ConfirmActionModal
        open={Boolean(confirmArchive)}
        title="Deactivate plan instead?"
        description="This plan is used by subscriptions and cannot be deleted. Deactivate it instead?"
        confirmLabel="Deactivate"
        onCancel={() => setConfirmArchive(null)}
        onConfirm={executeArchivePlan}
      />
    </div>
  );
}
